import React, { useEffect, useRef } from "react"
import Button from "../shared/Button"
import { ButtonSizes } from "../../shared/enums"
import { useVistCaseManager } from "../../lib/useVistCaseManager"
import { useVistCaseStore } from "../../stores/vistCaseStore"
import { ProgressCircle } from "../shared/ProgressCircle"
import { VistCase as BaseVistCase } from "../../lib/useS3Cases"
import "./S3VistCaseCard.css"

interface ExtendedVistCase extends BaseVistCase {
  isLocal?: boolean
  localData?: any
}

interface S3VistCaseCardProps {
  vistCase: ExtendedVistCase
}

export const S3VistCaseCard: React.FC<S3VistCaseCardProps> = ({
  vistCase
}) => {
  const isMountedRef = useRef(true)
  
  const formatModuleId = (moduleId: string) => {
    return moduleId
      .replace(/^module_id._?/, '') // Remove "module_id" and optional underscore from start
      .replace(/_/g, ' ') // Replace all underscores with spaces
      .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize first letter of each word
  };
  
  const { 
    downloadCase, 
    runCase,
    qtConnected
  } = useVistCaseManager()

  // Get state from store
  const downloadProgress = useVistCaseStore(state => state.downloadProgress)
  const downloadingCases = useVistCaseStore(state => state.downloadingCases)
  const runningCases = useVistCaseStore(state => state.runningCases)
  const allCases = useVistCaseStore(state => state.getCombinedCases())

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // Determine card state from store
  const getCardState = () => {
    if (runningCases.has(vistCase.jobId)) return 'running'
    if (downloadingCases.has(vistCase.jobId)) return 'downloading'
    
    const localCase = allCases.find(c => c.jobId === vistCase.jobId)
    if (localCase?.isLocal || vistCase.isLocal) return 'downloaded'
    
    return 'not_downloaded'
  }

  const cardState = getCardState()

  const handleDownload = async () => {
    if (!isMountedRef.current) return

    if (!qtConnected) {
      console.error('Qt not connected - cannot download')
      return
    }

    try {
      await downloadCase(vistCase.jobId)
    } catch (error: any) {
      console.error('Download failed:', error)
    }
  }

  const handleRun = async () => {
    if (!isMountedRef.current) return

    if (!qtConnected) {
      console.error('Qt not connected - cannot launch case')
      return
    }

    try {
      const sourceUrl = window.location.href
      console.log('Launching VistCase directly via VistCaseManager...')
      await runCase(vistCase.jobId, sourceUrl)
    } catch (error: any) {
      console.error('VistCase launch failed:', error)
    }
  }

  const getCardActions = () => {
    // If Qt is not connected and case is not downloaded (local), show appropriate message
    if (!qtConnected && cardState !== 'downloaded') {
      return (
        <div className="s3-vistcase-qt-disconnected">
          Qt Client Required
        </div>
      )
    }

    switch (cardState) {
      case 'not_downloaded':
        return (
          <Button size={ButtonSizes.Small} onClick={handleDownload}>
            Download
          </Button>
        )
      case 'downloading':
        const currentProgress = downloadProgress[vistCase.jobId] || 0
        return (
          <div className="s3-vistcase-download-progress">
            <ProgressCircle 
              progress={currentProgress}
              color="#ff7214"
            />
            <div className="s3-vistcase-download-text">
              Downloading...
            </div>
          </div>
        )
      case 'downloaded':
        return (
          <Button 
            size={ButtonSizes.Small} 
            onClick={handleRun}
          >
            Launch Case
          </Button>
        )
      case 'running':
        return (
          <div className="s3-vistcase-launch-progress">
            <ProgressCircle 
              progress={100} // Indeterminate progress
              color="#009966"
            />
            <div className="s3-vistcase-launch-text">
              Launching...
            </div>
          </div>
        )
      default:
        return (
          <Button size={ButtonSizes.Small} onClick={handleDownload}>
            Retry Download
          </Button>
        )
    }
  }

  const getStatusBadge = () => {
    // If Qt is not connected and case is not downloaded (local), show Qt Required
    if (!qtConnected && cardState !== 'downloaded') {
      return (
        <span className="s3-vistcase-badge s3-vistcase-badge--qt-disconnected">
          Qt Required
        </span>
      )
    }

    const badgeClass = `s3-vistcase-badge s3-vistcase-badge--${cardState.replace('_', '-')}`
    const badgeText = {
      not_downloaded: 'Cloud',
      downloading: 'Downloading',
      downloaded: 'Local',
      running: 'Running',
      error: 'Error'
    }
    
    return (
      <span className={badgeClass}>
        {badgeText[cardState]}
      </span>
    )
  }

  const renderMetadataBadges = () => {
    const badges: React.ReactNode[] = []
    
    if (vistCase.metadata.discipline_level && vistCase.metadata.discipline_level.length > 0) {
      vistCase.metadata.discipline_level.forEach((level, index) => {
        badges.push(
          <span key={`level-${index}`} className="s3-vistcase-metadata-badge s3-vistcase-metadata-badge--level">
            {level}
          </span>
        )
      })
    }
    
    if (vistCase.metadata.discipline_tags && vistCase.metadata.discipline_tags.length > 0) {
      vistCase.metadata.discipline_tags.forEach((tag, index) => {
        badges.push(
          <span key={`tag-${index}`} className="s3-vistcase-metadata-badge s3-vistcase-metadata-badge--tag">
            {tag}
          </span>
        )
      })
    }
    
    return badges.length > 0 ? badges : null
  }

  return (
    <div className="s3-vistcase-card">
      <div className="s3-vistcase-status-badge">
        {getStatusBadge()}
      </div>
      
      <div className="s3-vistcase-icon-container">
        {/* If thumbnail is not set, show a default icon */}
        {vistCase.metadata.thumbnail && <img className="s3-vistcase-icon" src={vistCase.metadata.thumbnail} alt="Vist case icon" />}
        {!vistCase.metadata.thumbnail && <img className="s3-vistcase-icon" src="/static/assets/brain.svg" alt="Vist case icon" />}
      </div>

      <div className="s3-vistcase-header">
        <h4 className="s3-vistcase-title">{vistCase.metadata.case_name}</h4>
        <div className="s3-vistcase-subheader">
          <span className="s3-vistcase-code s3-vistcase-author">Author: {vistCase.metadata.author || 'Unknown'}</span>
          {/* Hiding these for now due to too much confusion for end user 
          <span className="s3-vistcase-version">Version: {vistCase.metadata.module_version}</span>
          <span className="s3-vistcase-code">Module: {formatModuleId(vistCase.metadata.module_id)}</span> */}
        </div>
      </div>

      {/* Metadata badges container */}
      <div className="s3-vistcase-metadata-badges">
        {renderMetadataBadges()}
        {console.log(vistCase.metadata)}
      </div>
      
      <p className="s3-vistcase-description">{vistCase.metadata.description}</p>
      
      <div className="s3-vistcase-actions">
        {getCardActions()}
      </div>
    </div>
  )
}
